import {
  DEFAULT_LOGIN_FLOW_VARIANT,
  DialectsEnum,
  LangEnum,
  OSFamilyEnum,
  PlatformEnum,
} from "@/constants/constants";
import { ContinueWatchingContentInterface } from "@/types/home.types";
import { SettingsCopiesInterface } from "@/types/settingsCopies.types";
import * as UserInterfaces from "@/types/user.types";
import {
  captureError,
  generateContentDetailURL,
  getContentTypeUrlValue,
  getCurrentLang,
  getDialect,
  getPlanId,
  isTvEnv,
  parseEnum,
} from "@/utils/common.helpers";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";

import {
  SUBSCRIPTION_STATUS,
  UserSubscriptionStatusEnum,
  userSubscriptionStatusValues,
} from "@/constants/constants";
import { getOrCreateDeviceId } from "@/utils/deviceId";

export class UserAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async fetchUserSettings({
    dialect,
    lang,
    token,
  }: {
    dialect?: string;
    lang?: string;
    token?: string;
  } = {}) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.userSettings;
      const queryParams: Record<string, string | boolean | number> = {
        isTrialSubscriptionBuildUser: true,
        appBuildNumber: 505786, //TODO: Remove this once BE handle it
      };
      const finalLang = lang ?? (await getCurrentLang());
      const finalDialect = dialect ?? (await getDialect());

      if (isTvEnv()) {
        queryParams.platform = PlatformEnum.TV;
      }

      const response =
        await this.httpClient.request<UserInterfaces.UserSettingsInterface>({
          endpoint,
          options: {
            method,
          },
          queryParams,
          dialect: finalDialect,
          lang: finalLang,

          token,
        });

      return response.data;
    } catch (error) {
      console.error("Error while fetching user settings", error);
      throw captureError(new Error("Error while fetching user settings"));
    }
  }

  async fetchSettings() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getSettings;

      const response =
        await this.httpClient.request<UserInterfaces.SettingsInterface>({
          endpoint,
          options: {
            method,
          },
          addToken: false,
        });

      return response.data;
    } catch (error) {
      console.error("Error while fetching settings", error);
      throw captureError(error);
    }
  }

  async fetchSettingsCopies(params?: {
    overridingPlanId?: string;
    lang?: string;
    dialect?: string;
    loginFlowVariant?: string;
  }) {
    try {
      const { overridingPlanId, lang, dialect } = params ?? {};
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getSettingsCopies;

      const loginFlowVariant =
        params?.loginFlowVariant ?? DEFAULT_LOGIN_FLOW_VARIANT;

      const queryParams: Record<string, string | boolean | number> = {
        isRecurringSubscriptionBuild: true,
        isTrialSubscriptionBuildUser: true,
        appId: "in.stage",
        tnplPlanId: overridingPlanId ?? (await getPlanId()),
        ...(loginFlowVariant ? { loginFlowVariant } : {}),
        os: OSFamilyEnum.ANDROID,
      };

      const finalLang = parseEnum(LangEnum, lang);

      const response = await this.httpClient.request<SettingsCopiesInterface>({
        endpoint,
        options: {
          method,
        },
        queryParams,
        addToken: false,
        lang: finalLang ?? LangEnum.EN,
        dialect,
      });

      return response.data;
    } catch (error) {
      console.error("Error while fetching settings Copies", error);
      throw captureError(error);
    }
  }

  async fetchUserSubscriptionStatus({
    dialect,
    lang,
  }: {
    dialect?: string;
    lang?: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getUserSubscriptionStatus;
      const deviceId = await getOrCreateDeviceId();
      const queryParams = {
        isRenew: false,
        isRecurringSubscriptionBuild: true,
        deviceId,
      };

      const response =
        await this.httpClient.request<UserInterfaces.UserSubscriptionStatus>({
          endpoint,
          options: {
            method,
          },
          queryParams,
          dialect,
          lang,
        });

      return {
        subscriptionStatus: response?.data?.subscriptionStatus | 0,
        isGlobal: response?.data?.isGlobal ?? false,
      };
    } catch (error) {
      console.error("Error while fetching user subscription status", error);
      throw captureError(error);
    }
  }

  async fetchSubscriptionGlobalStatus({
    dialect,
    lang,
  }: {
    dialect?: string;
    lang?: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getSubscriptionGlobalStatus;
      const deviceId = await getOrCreateDeviceId();
      const queryParams = {
        isRenew: false,
        isRecurringSubscriptionBuild: true,
        deviceId,
      };

      const response =
        await this.httpClient.request<UserInterfaces.UserSubscriptionStatus>({
          endpoint,
          options: {
            method,
          },
          queryParams,
          dialect,
          lang,
        });

      return {
        isGlobal: response?.data?.isGlobal ?? false,
      };
    } catch (error) {
      console.error("Error while fetching subscription global status", error);
      captureError(error);
      return { isGlobal: false };
    }
  }

  async getOTP({
    mobileNumber,
    dialect,
    lang,
    countryCode,
    country,
  }: {
    mobileNumber: string;
    dialect: string;
    lang: string;
    countryCode?: string;
    country?: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.getOTP;

      const queryParams: Record<string, string> = {};

      if (isTvEnv()) {
        queryParams.platform = PlatformEnum.TV;
      }

      const deviceId = await getOrCreateDeviceId();
      const response =
        await this.httpClient.request<UserInterfaces.GetOTPResponseInterface>({
          endpoint,
          options: {
            method,
          },
          dialect,
          lang,
          queryParams,
          body: {
            lang: "hin",
            type: "web",
            deviceId,
            mobileNumber,
            ...(countryCode && { countryCode }),
            ...(country && { country }),
          },
          addToken: false,
        });

      if (response?.responseCode !== 200) {
        return {
          errorMessage:
            response.data?.toastMessage ?? response?.responseMessage,
        };
      }

      return { userId: response?.data?._id };
    } catch (error) {
      console.error("Error in UserAPIClient getOTP", error);

      throw captureError(error);
    }
  }

  async verifyOTP({
    id,
    mobileNumber,
    otp,
    dialect,
    lang,
  }: {
    id: string;
    mobileNumber: string;
    otp: string;
    dialect: string;
    lang: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.verifyOTP;

      const queryParams: Record<string, string> = {};

      if (isTvEnv()) {
        queryParams.platform = PlatformEnum.TV;
      }

      const response =
        await this.httpClient.request<UserInterfaces.VerifyOTPResponseInterface>(
          {
            endpoint,
            options: {
              method,
            },
            dialect,
            lang,
            queryParams,
            body: {
              id,
              mobileNumber,
              otp,
            },
            addToken: false,
          },
        );

      return response;
    } catch (error) {
      console.error("Error in UserAPIClient verifyOTP", error);

      throw new Error("कृपया सही ओ टी पी दर्ज करें!");
    }
  }

  async fetchTrialPlans(params?: {
    overridingPlanId?: string;
    dialect?: string;
  }) {
    try {
      const { overridingPlanId, dialect } = params ?? {};
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getTrialPlans;

      const response =
        await this.httpClient.request<UserInterfaces.TrialPlansResponse>({
          endpoint,
          options: {
            method,
          },
          dialect,
        });

      const planId = overridingPlanId ?? (await getPlanId());
      return response.data.trialPlans.findLast(
        (trialPlan) => trialPlan.planId === planId,
      );
    } catch (error) {
      console.error("Error while fetching user trial plans", error);
      throw captureError(error);
    }
  }

  //poll for truecaller success status after the user has verified the profile
  async fetchTruecallerProfileStatus({
    uniqueRequestId,
  }: {
    uniqueRequestId: string;
  }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.verifyTruecallerProfileStatus;
      // ?requestId=91c58a8258a94a63acd231ca0c4c8a07

      const response =
        await this.httpClient.request<UserInterfaces.TruecallerProfileStatusInterface>(
          {
            endpoint,
            options: {
              method,
            },
            queryParams: {
              requestId: uniqueRequestId,
            },
            addToken: false,
          },
        );

      return response.data;
    } catch (error) {
      console.error("Error while polling for truecaller success", error);
      return { verifiedProfileStatus: "FAILURE", requestId: "" };
      // throw new Error("Error while polling for truecaller success");
    }
  }

  async socialLogin({
    requestId,
    dialect,
    lang,
  }: {
    requestId: string;
    dialect: string;
    lang: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.socialLogin;
      // ?requestId=91c58a8258a94a63acd231ca0c4c8a07

      const deviceId = await getOrCreateDeviceId();
      const response =
        await this.httpClient.request<UserInterfaces.VerifyOTPResponseInterface>(
          {
            endpoint,
            options: {
              method,
            },
            dialect,
            lang,
            queryParams: {
              newTokenRequired: true,
              appId: "in.stage",
            },
            body: {
              type: "web",
              lang: "hin",
              provider: "mobilenumber", // truecaller's mobilenumber to keep it consistent with the existing otp flow
              truecallerRequestId: requestId,
              deviceId,
            },
            addToken: false,
          },
        );

      return response?.data;
    } catch (error) {
      console.error("Error while polling for truecaller success", error);
      throw new Error("Error while polling for truecaller success");
    }
  }

  async getMyList({ lang, dialect }: { lang: string; dialect: string }) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.myList;

      const response =
        await this.httpClient.paatarServerRequest<UserInterfaces.MyListResponse>(
          {
            endpoint,
            options: {
              method,
            },
          },
        );

      if (!response.status) {
        throw new Error(response.errorMessage);
      }

      const mappedItems = response.data.items.map((item) => {
        const contentTypeUrlValue = getContentTypeUrlValue(
          item.contentType,
          item.showFormat,
        );
        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          contentTypeUrlValue,
          item.slugDisplay,
        );

        return {
          ...item,
          _id: item.contentID,
          type: item.contentType,
          title: item.title,
          contentUrl,
          slug: item.contentSlug,
          horizontalThumbnail: item.thumbnail.horizontal.url,
          verticalThumbnail: item.thumbnail.vertical.url,
          squareThumbnail: "",
        } as ContinueWatchingContentInterface;
      });

      return {
        ...response.data,
        items: mappedItems,
      } as UserInterfaces.MappedMyListResponse;
    } catch (error) {
      console.error("Error while fetching my list", error);
      captureError(error);
      return {
        error: true,
      };
    }
  }

  async addMyList(
    contentID: number,
    contentType: string,
    lang: string,
    dialect: string,
  ) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.myList;

      const items = [
        {
          contentID,
          contentType,
        },
      ];

      const response =
        await this.httpClient.paatarServerRequest<UserInterfaces.MyListResponse>(
          {
            endpoint,
            options: {
              method,
            },
            body: items,
            lang,
            dialect,
          },
        );

      return response.status;
    } catch (error) {
      console.error("Error while adding to my list", error);
      captureError(error);
      return false;
    }
  }

  async removeMyList(
    contentID: number,
    contentType: string,
    lang: string,
    dialect: string,
  ) {
    try {
      const method = REQUEST_METHODS.DELETE;
      const endpoint = API_ROUTES.user.myList;

      const items = [
        {
          contentID,
          contentType,
        },
      ];

      const response =
        await this.httpClient.paatarServerRequest<UserInterfaces.MyListResponse>(
          {
            endpoint,
            options: {
              method,
            },
            body: items,
            lang,
            dialect,
          },
        );

      return response.status;
    } catch (error) {
      console.error("Error while removing from my list", error);
      captureError(error);
      return false;
    }
  }
  // Do not remove this method. It will be needed in the future when the old subscription flow is deprecated.
  async fetchUserSubscriptionStatusV2() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getUserSubscriptionStatusV2;

      const response = await this.httpClient.nestRequest<{
        status: UserSubscriptionStatusEnum;
      }>({
        endpoint,
        options: {
          method,
        },
      });

      return userSubscriptionStatusValues[response.status];
    } catch (error) {
      console.error("Error while fetching user subscription statusv2", error);
      captureError(error);
      return SUBSCRIPTION_STATUS.NOT_SUBSCRIBED;
    }
  }

  async updateUserCulture(userCulture: string, dialect: string, lang?: string) {
    try {
      const method = REQUEST_METHODS.PATCH;
      const endpoint = API_ROUTES.user.updateUserCulture;

      const response = await this.httpClient.nestRequest({
        endpoint,
        options: {
          method,
        },
        body: { userCulture },
        dialect,
        lang,
      });
    } catch (error) {
      console.error("Error in SettingAPIClient updateUserCulture", error);
      throw captureError(error);
    }
  }

  async updateUserDialect(dialect: string, lang?: string) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.updateUserDialect;
      const finalLang = lang ?? (await getCurrentLang());

      const response = await this.httpClient.request({
        endpoint,
        options: {
          method,
        },
        dialect,
        lang: finalLang,
        body: {
          lang: finalLang,
          dialect,
        },
      });
    } catch (error) {
      console.error("Error in UserAPIClient updateUserDialect", error);
      throw captureError(error);
    }
  }

  async updateGAClientId(analyticsId: string) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.event.updateUserProperties;

      const response = await this.httpClient.request({
        endpoint,
        options: {
          method,
        },
        body: {
          firebaseClientId: analyticsId,
        },
      });
    } catch (error) {
      console.error("Error in UserAPIClient updateFirebaseAnalyticsId", error);
    }
  }

  async fetchUserLocation() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getLocation;
      const response =
        await this.httpClient.request<UserInterfaces.UserLocationResponse>({
          endpoint,
          options: { method },
        });

      return response.data;
    } catch (error) {
      console.error("Error in UserAPIClient fetchUserLocation", error);
      captureError(error);
      return { country: "IN", city: "" };
    }
  }

  async fetchUserProfiles(token?: string) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.profiles;

      const response = await this.httpClient.nestRequest<
        UserInterfaces.Profile[]
      >({
        endpoint,
        options: { method },
        token,
      });

      return response;
    } catch (error) {
      console.error("Error in UserAPIClient fetchUserProfiles", error);
      captureError(error);
      return [];
    }
  }

  async getSwitchProfilesUrl(profileId: string) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.user.getSwitchProfilesUrl(profileId);

      const response = await this.httpClient.nestRequest<{
        token: string;
        userProfile: UserInterfaces.Profile;
      }>({
        endpoint,
        options: { method },
        // the api is so wonderfully designed, we're forced to send an empty object as body or change the content type header - truly a masterpiece of API architecture
        body: {},
      });
      const data = {
        ...response,
        token: response.token.split(" ")[1],
      };
      return data;
    } catch (error) {
      console.error("Error in UserAPIClient getSwitchProfilesUrl", error);
      captureError(error);
      return {
        token: "",
        userProfile: null,
      };
    }
  }

  async createProfile({
    profileName,
    avatar,
    contentCulture,
    language,
    isEditFlow = false,
    currentProfile,
  }: {
    profileName: string;
    avatar: string;
    contentCulture: string;
    language: string;
    isEditFlow?: boolean;
    currentProfile?: UserInterfaces.Profile;
  }) {
    try {
      const method = isEditFlow ? REQUEST_METHODS.PATCH : REQUEST_METHODS.POST;
      const endpoint =
        isEditFlow && currentProfile
          ? API_ROUTES.user.getProfileUrl(currentProfile.profileId)
          : API_ROUTES.user.profiles;

      const body = {
        displayName: profileName,
        avatar,
        contentCulture,
        language,
      };

      const response =
        await this.httpClient.nestRequest<UserInterfaces.Profile>({
          endpoint,
          options: { method },
          body,
        });

      return response;
    } catch (error) {
      console.error("Error in UserAPIClient createProfile", error);
      captureError(error);
      throw error;
    }
  }

  async deleteProfile(profileId: string) {
    try {
      const method = REQUEST_METHODS.DELETE;
      const endpoint = API_ROUTES.user.getProfileUrl(profileId);

      const response = await this.httpClient.nestRequest({
        endpoint,
        options: { method },
        // the api is so wonderfully designed, we're forced to send an empty object as body or change the content type header - truly a masterpiece of API architecture
        body: {},
      });

      return response;
    } catch (error) {
      if (error?.toString().includes("Unexpected end of JSON input")) {
        // API returns 200 with empty response body, causing res.json() to throw "Unexpected end of JSON input"
        // We handle this by returning true to indicate successful profile deletion
        return true;
      }

      console.error("Error in UserAPIClient deleteProfile", error);
      captureError(error);
      throw error;
    }
  }

  async fetchProfileFeatureContent() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getProfileFeatureContent;

      const response =
        await this.httpClient.nestRequest<UserInterfaces.ProfileFeatureContentResponse>(
          {
            endpoint,
            options: { method },
          },
        );
      return response.data.items;
    } catch (error) {
      console.error("Error in UserAPIClient fetchProfileFeatureContent", error);
      captureError(error);
      return [];
    }
  }

  async fetchUserSubscriptionMandateStatus(token?: string) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.user.getUserSubscriptionMandateStatus;

      const response = await this.httpClient.nestRequest<{
        mandate_status: string;
        subscription_status: string;
        subscription_valid: string;
        trial_days_elapsed?: number;
      }>({
        endpoint,
        options: {
          method,
        },
        token,
      });

      return response;
    } catch (error) {
      console.error("Error while fetching user subscription statusv2", error);
      captureError(error);
      return {
        mandate_status: "",
        subscription_status: "",
        subscription_valid: "",
        trial_days_elapsed: undefined,
      };
    }
  }
}
